// Gateway — the home screen after login. Orenza aesthetic, role-aware tiles.
// First-run checklist + today's snapshot + quick voucher actions + section cards.

import type React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Money } from '@/components/primitives/Money';
import { KbdChip } from '@/components/primitives/KbdChip';
import { AppShell } from '@/components/AppShell';
import { useAuthStore } from '@/store/auth';
import { useChaiBrief } from '@/api/reports';
import { useVouchers } from '@/api/vouchers';
import { api } from '@/lib/api';
import { cleanErr } from '@/lib/errMsg';
import { formatDateDDMMMYYYY, pluralize } from '@/utils/format';
import { cn } from '@/lib/cn';
import { navigateTo } from '@/lib/navigate';

export interface Tile {
  title: string;
  description: string;
  shortcut?: string;
  warm?: boolean;
  href?: string;
}

export const MASTERS: Tile[] = [
  { title: 'Parties', description: 'Customers, vendors & distributors', shortcut: 'Alt+P', href: '/masters/parties' },
  { title: 'Items', description: 'Goods, services & fixed assets', shortcut: 'Alt+I', href: '/masters/items' },
  { title: 'Ledgers', description: 'Chart of accounts — every ledger', shortcut: 'Alt+Shift+L', href: '/masters/ledgers' },
  { title: 'Voucher Types', description: 'System + custom voucher series', shortcut: 'Alt+V', href: '/masters/voucher-types' },
  { title: 'HSN / SAC', description: 'GST classification codes with default rates', shortcut: 'Alt+H', href: '/masters/hsn-sac' },
  { title: 'Tax Rates', description: 'Effective-dated GST / Cess / TDS / TCS', href: '/masters/tax-rates' },
  { title: 'Price Levels', description: 'Wholesale / Retail rate tiers + price lists', href: '/masters/price-levels' },
  { title: 'Units', description: 'kg / pcs / ltr — base + compound units', href: '/masters/units' },
  { title: 'Stock Groups', description: 'Hierarchical item categorisation', href: '/masters/stock-groups' },
  { title: 'Cost Centres', description: 'Hierarchical buckets for management accounting', href: '/masters/cost-centres' },
  { title: 'Cost Categories', description: 'Tags for cost centres (Dept / Project / Region)', href: '/masters/cost-categories' },
];

export const OPERATIONS: Tile[] = [
  { title: 'Bank Import', description: 'HDFC / ICICI / SBI / Axis CSV — parse + auto-post', href: '/banking/import' },
  { title: 'Bank Reconciliation', description: 'Match statement vs books · find gaps · month-end', href: '/banking/reconcile' },
  { title: 'Tally Outbox', description: 'Queue of vouchers awaiting Tally sync agent', href: '/admin/tally-outbox' },
  { title: 'e-Invoice Queue', description: 'IRN push to NIC IRP · 30-day watchdog', href: '/admin/e-invoice-queue' },
];

export const ADMIN: Tile[] = [
  { title: 'Tenant Portal', description: 'Companies, setup status, re-seed, back-fill', shortcut: 'Alt+A', href: '/admin' },
  { title: 'Users',         description: 'Invite, suspend, role + password reset',        shortcut: '', href: '/admin/users' },
  { title: 'Branches',      description: 'Per-company GSTIN + state + HQ flag',           shortcut: '', href: '/admin/branches' },
  { title: 'Audit Log',     description: 'MCA Rule 3(1) immutable trail',                 shortcut: '', href: '/admin/audit-log' },
  { title: 'Accountant Portal', description: 'Invite CAs, compliance calendar, audit handoff', shortcut: '', href: '/admin/ca-portal' },
  { title: 'Subscription',  description: 'Plan, usage, billing history', shortcut: '', href: '/admin/subscription' },
  { title: 'Posting Periods', description: 'Year-end close + monthly close locks', shortcut: '', href: '/admin/posting-periods' },
  { title: 'SOC 2 Readiness', description: 'Trust service criteria — live evaluation', shortcut: '', href: '/admin/soc2' },
  { title: 'Group Companies', description: 'Cluster companies · consolidated TB + P&L', shortcut: '', href: '/admin/group-companies' },
  { title: 'Security Policy', description: '2FA · IP allow-list · session controls', shortcut: '', href: '/admin/security-policy' },
  { title: 'Statutory Updates', description: 'Gazette → ship · 48-hour SLA tracker', shortcut: '', href: '/admin/statutory' },
  { title: 'Suspicious Activity', description: '8-rule engine · severity-ranked alerts', shortcut: '', href: '/admin/suspicious-activity' },
  { title: 'Performance', description: 'p50/p95/p99 · error rate · per-route', shortcut: '', href: '/admin/performance' },
  { title: 'Tally Vault', description: 'AES-256-GCM per-company at-rest encryption', shortcut: '', href: '/admin/vault' },
  { title: 'SOC 2 Evidence', description: 'Auditor-ready artefact vault by control code', shortcut: '', href: '/admin/soc2/evidence' },
  { title: 'Year-End Close', description: 'P&L → Retained Earnings · hard-close FY', shortcut: '', href: '/admin/year-end' },
  { title: 'Background Jobs', description: 'e-Invoice · e-Way · webhooks · Tally outbox', shortcut: '', href: '/admin/jobs' },
  { title: 'Data Retention', description: 'Auto-purge policy · run sweep', shortcut: '', href: '/admin/retention' },
  { title: 'Approvals', description: 'Pending voucher second-signoff inbox', shortcut: '', href: '/admin/approvals' },
  { title: 'Rate Limits', description: 'Per-route override · abuse cooldown', shortcut: '', href: '/admin/rate-limits' },
  { title: 'Help · keyboard', description: 'Every shortcut, grouped by surface',          shortcut: 'F1', href: '/help' },
];

export function GatewayPage() {
  const user = useAuthStore((s) => s.user);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const companies = useAuthStore((s) => s.companies);
  const today = new Date();

  const needsCompanyPick = !activeCompanyId && companies.length > 0;
  const needsFirstCompany = companies.length === 0;

  // Single-letter quick actions (advertised on the voucher buttons below).
  // Only fire outside form fields, and only while the Gateway is mounted.
  const go = (path: string) => (e: KeyboardEvent) => { e.preventDefault(); navigateTo(path); };
  useHotkeys('s', go('/vouchers/new/sales'),       { enableOnFormTags: false });
  useHotkeys('p', go('/vouchers/new/purchase'),    { enableOnFormTags: false });
  useHotkeys('c', go('/vouchers/new/receipt'),     { enableOnFormTags: false });
  useHotkeys('y', go('/vouchers/new/payment'),     { enableOnFormTags: false });
  useHotkeys('x', go('/vouchers/new/contra'),      { enableOnFormTags: false });
  useHotkeys('n', go('/vouchers/new/credit-note'), { enableOnFormTags: false });
  useHotkeys('d', go('/vouchers/new/debit-note'),  { enableOnFormTags: false });
  useHotkeys('j', go('/vouchers/new/journal'),     { enableOnFormTags: false });
  // Section jumps (Gateway only). 'b' stays BI; Banking gets 'k' (banKing) since
  // 'b' was already taken — so every section tab has a distinct, advertised key.
  useHotkeys('v', go('/vouchers'),                 { enableOnFormTags: false });
  useHotkeys('r', go('/reports'),                  { enableOnFormTags: false });
  useHotkeys('b', go('/bi'),                       { enableOnFormTags: false });
  useHotkeys('k', go('/banking'),                  { enableOnFormTags: false });
  useHotkeys('m', go('/masters'),                  { enableOnFormTags: false });
  useHotkeys('a', go('/admin'),                    { enableOnFormTags: false });

  return (
    <AppShell>
      <>
        {/* ── Hero (Design System v2) ────────────────────────────────────────
            The reference for the whole product: signature indigo→violet field,
            branded eyebrow rail, display type at full scale, the user's name in
            the brand gradient. Asymmetric, energetic, unmistakably the home. */}
        <section className="relative mb-8 overflow-hidden rounded-lg border border-mist bg-frost px-6 py-8 shadow-card sm:mb-10 sm:px-8 sm:py-10">
          {/* Signature field — indigo→violet bleeding from the right. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-20 h-72 w-[38rem] max-w-[130%] rounded-full opacity-80 blur-3xl"
            style={{ background: 'var(--wash-hero)' }}
          />
          <div className="relative">
            <div className="mb-3 flex items-center gap-2.5">
              <span className="h-[3px] w-9 rounded-full" style={{ background: 'var(--brand-grad)' }} />
              <span className="font-mono text-nano font-medium uppercase tracking-[0.18em] text-cobalt">
                {formatDateDDMMMYYYY(today)}
              </span>
            </div>
            <h1 className="font-display text-display-lg font-semibold text-obsidian sm:text-display-xl">
              Welcome back,{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'var(--brand-grad)' }}
              >
                {user?.name?.split(' ')[0] ?? 'there'}
              </span>
            </h1>
            <p className="mt-2.5 max-w-[54ch] text-body-md text-slate sm:text-body-lg">
              Your books, live. Press a letter to jump straight into entry.
            </p>
          </div>
        </section>

        {/* Onboarding callouts — show when active company isn't set */}
        {needsFirstCompany && (
          <section className="mb-8 rounded-md border border-citrus/40 bg-citrus/[0.05] p-5">
            <h2 className="font-display text-display-sm font-semibold text-obsidian">
              Create your first company to get started
            </h2>
            <p className="mt-1 text-body-md text-slate">
              A company is the book of accounts for one legal entity. You can run multiple companies side-by-side from this workspace.
              Use the orange button in the top bar to create one — chart of accounts and voucher types seed automatically.
            </p>
          </section>
        )}
        {needsCompanyPick && (
          <section className="mb-8 rounded-md border border-cobalt/40 bg-cobalt/[0.05] p-5">
            <h2 className="font-display text-display-sm font-semibold text-obsidian">
              Pick a company to continue
            </h2>
            <p className="mt-1 text-body-md text-slate">
              You have access to {companies.length} compan{companies.length === 1 ? 'y' : 'ies'}.
              Choose one from the picker in the top bar — Parties / Items / Ledgers / Vouchers all scope to the active company.
            </p>
          </section>
        )}

        {/* First-run guided checklist — shows until the books have a party, an item and a voucher */}
        {activeCompanyId && <FirstRunChecklist />}

        {/* Today's snapshot strip — live from JournalLine + BillRef */}
        <ChaiBriefStrip />

        {/* Recent vouchers strip — last 3 posted */}
        <RecentVouchersStrip />

        {/* Quick actions — start a new voucher in one click */}
        <section className="mb-10">
          <div className="mb-3 flex items-baseline gap-3">
            <h2 className="flex items-center text-micro font-medium uppercase tracking-[0.16em] text-obsidian">
              <span className="mr-3 inline-block h-[2px] w-6 bg-cobalt" />
              Post a voucher
            </h2>
            <span className="hidden text-caption text-cool sm:inline">· Skip the list — go straight to entry</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            <QuickAction label="Sales"       href="/vouchers/new/sales"        shortcut="S"  tone="in" />
            <QuickAction label="Purchase"    href="/vouchers/new/purchase"     shortcut="P"  tone="out" />
            <QuickAction label="Receipt"     href="/vouchers/new/receipt"      shortcut="C"  tone="in" />
            <QuickAction label="Payment"     href="/vouchers/new/payment"      shortcut="Y"  tone="out" />
            <QuickAction label="Contra"      href="/vouchers/new/contra"       shortcut="X"  tone="adj" />
            <QuickAction label="Journal"     href="/vouchers/new/journal"      shortcut="J"  tone="adj" />
            <QuickAction label="Credit Note" href="/vouchers/new/credit-note"  shortcut="N"  tone="corr" />
            <QuickAction label="Debit Note"  href="/vouchers/new/debit-note"   shortcut="D"  tone="corr" />
          </div>
        </section>

        {/* Explore — five section cards (replaces the 39-tile dump) */}
        <section className="mb-10">
          <div className="mb-3 flex items-baseline gap-3">
            <h2 className="flex items-center text-micro font-medium uppercase tracking-[0.16em] text-obsidian">
              <span className="mr-3 inline-block h-[2px] w-6 bg-cobalt" />
              Explore
            </h2>
            <span className="hidden text-caption text-cool sm:inline">· or press <KbdChip>Ctrl K</KbdChip> to jump to anything</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SectionCard href="/vouchers" title="Vouchers" tone="cobalt"
              description="Sales · Purchase · Receipt · Payment · Contra · Journal · CN · DN"
              count="8 voucher types" shortcut="V" />
            <SectionCard href="/reports" title="Reports" tone="cobalt"
              description="Day Book · Trial Balance · P&L · BS · GSTR · Outstanding + 12 more"
              count="20+ curated" shortcut="R" />
            <SectionCard href="/bi" title="Business Intelligence" tone="cobalt"
              description="Dashboards over live books · CEO Cockpit · Sales 360° · Cash Flow"
              count="10 dashboards" shortcut="B" featured />
            <SectionCard href="/reports-catalogue" title="Reports Catalogue" tone="cobalt"
              description="300+ ready reports — every one runs on your books, drills to vouchers"
              count="300+ live" featured />
            <SectionCard href="/masters" title="Masters" tone="cobalt"
              description="Parties · Items · Ledgers · Voucher Types · HSN/SAC · Tax Rates"
              count="10 masters" shortcut="M" />
            <SectionCard href="/banking" title="Banking" tone="cobalt"
              description="Import · Reconcile · HDFC/ICICI/SBI/Axis statement parsers"
              count="2 surfaces" shortcut="K" />
            <SectionCard href="/admin" title="Admin" tone="cobalt"
              description="Tenant · Users · Branches · Audit Log · Security · Subscription"
              count="20+ admin" shortcut="A" />
            <SectionCard href="/help" title="Keyboard help" tone="cobalt"
              description="Every shortcut, grouped by surface — Tally parity included"
              count="" shortcut="F1" muted />
          </div>
        </section>

        {/* Footer hint */}
        <footer className="mt-10 flex flex-col items-start gap-2 border-t border-mist pt-4 text-caption text-cool sm:mt-16 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Press <KbdChip>Ctrl K</KbdChip> to open the command palette</span>
            <span className="hidden sm:inline">·</span>
            <span><KbdChip>F8</KbdChip> new Sales invoice</span>
            <span className="hidden sm:inline">·</span>
            <span><KbdChip>F1</KbdChip> for keyboard help</span>
          </div>
          <span data-numeric className="font-mono text-nano text-cool">v{__APP_VERSION__}</span>
        </footer>
      </>
    </AppShell>
  );
}

// First-run guided checklist: three steps to a working set of books, plus one-click
// sample data for people who'd rather explore first. Disappears once all steps are done.
function FirstRunChecklist() {
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ['first-run-status', activeCompanyId],
    queryFn: async () => {
      const [parties, items, vouchers] = await Promise.all([
        api.get<{ pagination: { total: number } }>('/masters/parties', { params: { page: 1, pageSize: 1 } }),
        api.get<{ pagination: { total: number } }>('/masters/items', { params: { page: 1, pageSize: 1 } }),
        api.get<{ pagination: { total: number } }>('/vouchers', { params: { page: 1, pageSize: 1 } }),
      ]);
      return {
        parties: parties.data.pagination?.total ?? 0,
        items: items.data.pagination?.total ?? 0,
        vouchers: vouchers.data.pagination?.total ?? 0,
      };
    },
    staleTime: 30_000,
    retry: false,
  });

  const seed = useMutation({
    mutationFn: async () => (await api.post('/admin/sample-seed')).data,
    onSuccess: async () => { await qc.invalidateQueries(); },
  });

  const s = status.data;
  if (!s) return null;
  const steps = [
    { done: s.parties > 0,  label: 'Add your first party',       detail: 'A customer you sell to, or a vendor you buy from', href: '/masters/parties' },
    { done: s.items > 0,    label: 'Add your first item',        detail: 'The goods or services on your invoices',           href: '/masters/items' },
    { done: s.vouchers > 0, label: 'Post your first Sales invoice', detail: 'Party + items — the PDF is one click after saving', href: '/vouchers/new/sales' },
  ];
  const doneCount = steps.filter((x) => x.done).length;
  if (doneCount === steps.length) return null;

  return (
    <section className="mb-8 overflow-hidden rounded-md border border-cobalt/20 bg-frost">
      <div className="h-1 bg-gradient-to-r from-cobalt via-cobalt to-citrus" aria-hidden />
      <div className="p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-display-sm font-semibold text-obsidian">
            Set up your books — {doneCount} of {steps.length} done
          </h2>
          <span className="text-caption text-cool">takes about 2 minutes</span>
        </div>
        <ol className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {steps.map((step, i) => (
            <li key={step.label}>
              <a
                href={step.done ? undefined : step.href}
                className={cn(
                  'flex h-full flex-col gap-1 rounded-md border p-3 transition',
                  step.done
                    ? 'border-emerald/40 bg-emerald/[0.05]'
                    : 'border-mist bg-snow hover:border-cobalt hover:-translate-y-0.5 hover:shadow-card',
                )}
              >
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-nano font-semibold',
                    step.done ? 'bg-emerald text-snow' : 'bg-cobalt/10 text-cobalt',
                  )}>
                    {step.done ? '✓' : i + 1}
                  </span>
                  <span className={cn('text-body-sm font-medium', step.done ? 'text-emerald line-through decoration-emerald/40' : 'text-obsidian')}>
                    {step.label}
                  </span>
                </div>
                <span className="pl-7 text-caption text-slate">{step.detail}</span>
              </a>
            </li>
          ))}
        </ol>
        {s.parties === 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-mist pt-3">
            <span className="text-body-sm text-slate">Prefer to explore first?</span>
            <button
              type="button"
              onClick={() => seed.mutate()}
              disabled={seed.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-citrus/40 bg-citrus/10 px-3 py-1.5 text-body-sm font-medium text-obsidian transition hover:bg-citrus/20 disabled:opacity-60"
            >
              {seed.isPending ? 'Loading sample data…' : 'Load sample data'}
            </button>
            <span className="text-caption text-cool">Adds a few parties, items and vouchers so every screen has something to show.</span>
            {seed.isError && <span className="w-full text-caption text-crimson">{cleanErr(seed.error, 'Could not load sample data')}</span>}
          </div>
        )}
      </div>
    </section>
  );
}

// v1.6.0 — SectionCard used to offer only cobalt|slate, so five of the seven cards
// on the Gateway were the SAME blue: Reports, BI, Catalogue, Banking and Admin. The
// home screen was the clearest example of the app reading as one flat colour.
// Each section now carries its own identity hue (see theme/sections.ts).
function SectionCard({ href, title, description, count, shortcut, muted, tone, featured }: {
  href: string; title: string; description: string; count: string; shortcut?: string; muted?: boolean;
  tone?: 'cobalt' | 'slate' | 'citrus' | 'emerald' | 'violet';
  featured?: boolean;
}) {
  // Gradient alphas sit near 10–14%: the v1.3.0 washes were 3.5–6%, which is below
  // the threshold where a tint reads as colour on a near-white card.
  // v1.7.0 — five hues collapsed to the warm/cool pair. Violet and emerald as
  // DECORATION are gone; emerald survives only where it means money-in.
  const grad = {
    cobalt: 'from-cobalt/[0.11]',
    slate: 'from-cool/[0.09]',
    citrus: 'from-citrus/[0.16]',
    emerald: 'from-emerald/[0.12]',
    violet: 'from-cobalt/[0.11]',
  }[tone ?? 'cobalt'];
  const dot = {
    cobalt: 'bg-cobalt',
    slate: 'bg-cool',
    citrus: 'bg-citrus',
    emerald: 'bg-emerald',
    violet: 'bg-cobalt',
  }[tone ?? 'cobalt'];
  const hoverBorder = {
    cobalt: 'hover:border-cobalt',
    slate: 'hover:border-cool',
    citrus: 'hover:border-citrus',
    emerald: 'hover:border-emerald',
    violet: 'hover:border-cobalt',
  }[tone ?? 'cobalt'];
  // 2px identity rail across the top — the strongest, cheapest signal of "where am I".
  const rail = {
    cobalt: 'before:bg-cobalt',
    slate: 'before:bg-cool',
    citrus: 'before:bg-citrus',
    emerald: 'before:bg-emerald',
    violet: 'before:bg-cobalt',
  }[tone ?? 'cobalt'];
  return (
    <a
      href={href}
      className={cn(
        'group relative flex flex-col gap-2 overflow-hidden rounded-md border border-mist p-5 transition-all duration-150 ease-out',
        // Cards are white on the deeper Snow canvas now — they lift off the page
        // instead of dissolving into it (1.14:1 vs the old 1.05:1).
        muted ? 'bg-haze' : 'bg-frost',
        'before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:content-[""]',
        rail,
        hoverBorder,
        'hover:-translate-y-0.5 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-cobalt/40',
        featured && 'ring-1 ring-cobalt/20',
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-24 -z-0 bg-gradient-to-b to-transparent', grad)} aria-hidden />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={cn('h-2 w-2 rounded-full', dot)} aria-hidden />
          <h3 className="font-display text-display-sm font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>{title}</h3>
          {featured && <span className="inline-flex items-center h-4 px-1.5 rounded-sm bg-citrus/20 text-obsidian text-nano font-medium uppercase tracking-[0.12em]">New</span>}
        </div>
        {shortcut && <KbdChip>{shortcut}</KbdChip>}
      </div>
      <p className="relative text-body-sm text-slate">{description}</p>
      {count && <p className="relative mt-1 text-caption text-cool font-mono">{count}</p>}
      <span className="absolute right-5 bottom-4 text-cobalt opacity-0 transition group-hover:opacity-100" aria-hidden>→</span>
    </a>
  );
}

function Stat({
  label,
  value,
  important,
  placeholder,
  hint,
  count,
}: {
  label: string;
  value?: number | null;
  important?: boolean;
  placeholder?: string;
  hint?: string;
  count?: boolean; // plain tally, not rupees
}) {
  return (
    <div className="orz-lift rounded-md border border-mist bg-frost p-4 shadow-card sm:p-5">
      <div className="text-micro font-medium uppercase tracking-[0.12em] text-slate">{label}</div>
      {/* Whole rupees only — paise on a KPI card just makes ₹-figures clip in
          the 2-col 390px grid; the drill-through reports carry the exact value. */}
      <div className="mt-2 min-w-0 overflow-hidden">
        {typeof value === 'number' ? (
          count
            ? <span data-numeric className="font-mono tabular-nums text-display-sm sm:text-display-md text-obsidian">{value.toLocaleString('en-IN')}</span>
            : <Money amount={value} important={important} decimals={0} className="whitespace-nowrap text-body-lg sm:text-display-md" />
        ) : (
          <span data-numeric className="font-mono tabular-nums text-display-sm sm:text-display-md text-cool">{placeholder ?? '—'}</span>
        )}
      </div>
      {hint && <div className="mt-1 text-nano text-cool">{hint}</div>}
    </div>
  );
}

function RecentVouchersStrip() {
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const recentQ = useVouchers({ page: 1, pageSize: 5 });
  if (!activeCompanyId) return null;
  const rows = recentQ.data?.rows ?? [];
  if (rows.length === 0) return null;
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="flex items-center text-micro font-medium uppercase tracking-[0.16em] text-obsidian">
          <span className="mr-3 inline-block h-[2px] w-6 bg-slate" />
          Recent vouchers
        </h2>
        <span className="hidden text-caption text-cool sm:inline">· Click any to open detail</span>
        <a href="/vouchers" className="ml-auto text-caption text-cobalt hover:underline">View all →</a>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {rows.map((v) => (
          <a key={v.id} href={`/vouchers/${v.id}`}
            className="group flex flex-col gap-1 rounded-md border border-mist bg-frost p-3 transition hover:border-cobalt hover:shadow-card">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-caption text-cobalt truncate">{v.number}</span>
              <span className="rounded-sm bg-cobalt/10 px-1.5 py-0.5 text-nano font-medium uppercase tracking-[0.1em] text-cobalt">
                {v.voucherType?.abbreviation ?? '—'}
              </span>
            </div>
            <div className="text-body-sm text-obsidian truncate">{v.partyLedger?.name ?? '(no party)'}</div>
            <div className="flex items-baseline justify-between text-caption tabular-nums">
              <span className="text-cool">{new Date(v.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              <Money amount={Number(v.totalAmount)} className="text-obsidian font-medium" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function ChaiBriefStrip() {
  const brief = useChaiBrief();
  const d = brief.data;
  const isLoading = brief.isLoading && !d;
  return (
    <section className="orz-stagger mb-10 grid grid-cols-2 gap-3 sm:mb-12 sm:gap-4 lg:grid-cols-4">
      <Stat
        label="Cash on hand"
        value={isLoading || d?.needsCompanyPick ? null : d?.cashOnHand ?? 0}
        important
        placeholder="—"
        hint={d?.needsCompanyPick ? 'Pick a company in the top bar' : 'Sum of cash + bank ledgers'}
      />
      <Stat
        label="Today's collections"
        value={isLoading || d?.needsCompanyPick ? null : d?.todaysCollections ?? 0}
        important
        placeholder="—"
        hint="Money into cash/bank today"
      />
      <Stat
        label="Outstanding receivables"
        value={isLoading || d?.needsCompanyPick ? null : d?.receivables ?? 0}
        important
        placeholder="—"
        hint="Open bill-wise references"
      />
      <Stat
        label="Vouchers today"
        value={isLoading || d?.needsCompanyPick ? null : d?.vouchersToday ?? 0}
        count
        hint={d?.vouchersToday ? 'Open the Day Book to see them' : 'No vouchers posted today'}
      />
      <ChaiAlertStrip alerts={d?.alerts} />
    </section>
  );
}

function ChaiAlertStrip({ alerts }: { alerts?: import('@/api/reports').ChaiBriefResponse['alerts'] }) {
  if (!alerts) return null;
  const items: Array<{ kind: 'danger' | 'warn' | 'info'; href: string; head: string; body: string }> = [];
  if (alerts.msme43bh.disallowedAmount > 0) {
    items.push({
      kind: 'danger',
      href: '/reports/msme-payables',
      head: `MSME §43B(h) DISALLOWED — ₹${alerts.msme43bh.disallowedAmount.toLocaleString('en-IN')}`,
      body: `${pluralize(alerts.msme43bh.billCount, 'bill')} past the 45-day window. Settle before FY close or the expense is added back.`,
    });
  } else if (alerts.msme43bh.atRiskAmount > 0) {
    items.push({
      kind: 'warn',
      href: '/reports/msme-payables',
      head: `MSME §43B(h) at risk — ₹${alerts.msme43bh.atRiskAmount.toLocaleString('en-IN')}`,
      body: `${pluralize(alerts.msme43bh.billCount, 'bill')} within 5-day grace of the 45-day window. Settle now to avoid disallowance.`,
    });
  }
  if (alerts.tallyOutbox.failed > 0) {
    items.push({
      kind: 'danger',
      href: '/admin/tally-outbox',
      head: `Tally sync — ${alerts.tallyOutbox.failed} failed`,
      body: 'Review the outbox; CA workflow is blocked until cleared.',
    });
  } else if (alerts.tallyOutbox.pending > 50) {
    items.push({
      kind: 'warn',
      href: '/admin/tally-outbox',
      head: `Tally sync — ${alerts.tallyOutbox.pending} queued`,
      body: 'Agent is offline or behind. Check the Electron sync agent.',
    });
  }
  if (alerts.cashLedgersNegative > 0) {
    items.push({
      kind: 'warn',
      href: '/reports/trial-balance',
      head: `${alerts.cashLedgersNegative} cash/bank ledger${alerts.cashLedgersNegative === 1 ? '' : 's'} negative`,
      body: 'Possible overdraft or a data-entry error — open Trial Balance to inspect.',
    });
  }
  if (items.length === 0) return null;
  return (
    <div className="col-span-2 grid grid-cols-1 gap-2 lg:col-span-4 lg:grid-cols-3">
      {items.slice(0, 3).map((it, i) => (
        <a key={i} href={it.href} className={cn(
          'flex flex-col gap-1 rounded-md border p-3 text-body-sm transition hover:-translate-y-0.5 hover:shadow-card',
          it.kind === 'danger' ? 'border-crimson/40 bg-crimson/[0.05]' :
          it.kind === 'warn'   ? 'border-citrus/40 bg-citrus/[0.05]'  :
                                 'border-cobalt/40 bg-cobalt/[0.05]',
        )}>
          <div className={cn('font-medium',
            it.kind === 'danger' ? 'text-crimson' : it.kind === 'warn' ? 'text-citrus' : 'text-cobalt')}>
            {it.head}
          </div>
          <div className="text-caption text-slate">{it.body}</div>
        </a>
      ))}
    </div>
  );
}

// v1.6.0 — these eight were six identical cobalt buttons plus two grey ones, so the
// row carried no information beyond its labels. Colour now encodes DIRECTION OF MONEY,
// which is the thing an accountant actually scans for:
//   in   → emerald (Sales, Receipt)
//   out  → citrus  (Purchase, Payment)
//   adj  → slate   (Contra, Journal — money moving sideways, not in or out)
//   corr → cobalt  (Credit/Debit Note — corrections to a prior document)
// Tints are 8–10%: the 4% used before was invisible against the old near-white card.
// v2.2 — the screenshot review flagged this row as the worst offender: eight
// full-coloured boxes reading as random. Rebuilt CALM and UNIFORM: every button is a
// clean white card with the same border; direction of money is signalled by ONE small
// dot + the "+" tint, not by flooding the whole button with colour. The row now reads
// as one coherent control where colour is a quiet index, exactly like a professional
// tool. green=in · orange=out · indigo=correction · grey=adjustment.
function QuickAction({ label, href, shortcut, tone }: { label: string; href: string; shortcut?: string; tone: 'in' | 'out' | 'adj' | 'corr' }) {
  const dot = {
    in:   'bg-emerald',
    out:  'bg-citrus',
    adj:  'bg-cool',
    corr: 'bg-cobalt',
  }[tone];
  const hoverBorder = {
    in:   'hover:border-emerald/50',
    out:  'hover:border-citrus/50',
    adj:  'hover:border-cool',
    corr: 'hover:border-cobalt/50',
  }[tone];
  return (
    <a
      href={href}
      className={cn(
        'group flex items-center justify-between gap-2 rounded-md border border-mist bg-frost px-3 py-2.5 text-body-sm font-medium text-obsidian shadow-xs transition',
        'hover:-translate-y-px hover:shadow-card',
        hoverBorder,
      )}
    >
      <span className="flex items-center gap-2">
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot)} aria-hidden />
        {label}
      </span>
      {shortcut && <KbdChip>{shortcut}</KbdChip>}
    </a>
  );
}

export function TileGroup({ label, hint, kind, children }: { label: string; hint?: string; kind: 'cobalt' | 'citrus' | 'emerald' | 'violet' | 'slate'; children: React.ReactNode }) {
  // v1.6.0 — widened from 3 kinds to 5 so Masters (violet) and Admin (slate) can
  // carry their own identity instead of borrowing Reports' cobalt.
  const bar = {
    cobalt: 'bg-cobalt',
    citrus: 'bg-citrus',
    emerald: 'bg-emerald',
    violet: 'bg-violet',
    slate: 'bg-cool',
  }[kind];
  return (
    <section className="mb-10">
      <div className="mb-4 flex items-baseline gap-3">
        <h2 className="flex items-center text-micro font-medium uppercase tracking-[0.16em] text-obsidian">
          <span className={cn('mr-3 inline-block h-[2px] w-6', bar)} />
          {label}
        </h2>
        {hint && <span className="hidden text-caption text-cool sm:inline">· {hint}</span>}
      </div>
      {children}
    </section>
  );
}

export function Tile({
  title,
  description,
  shortcut,
  warm,
  href,
}: {
  title: string;
  description: string;
  shortcut?: string;
  warm?: boolean;
  href?: string;
}) {
  return (
    <a
      href={href ?? '#'}
      className={cn(
        'group relative flex flex-col gap-3 rounded-md border border-mist bg-frost p-5 transition-all duration-150 ease-out',
        'hover:border-cobalt hover:-translate-y-0.5 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-cobalt/40',
        warm && 'bg-citrus/[0.05]',
      )}
    >
      <div className="flex items-start justify-between">
        <h3 className="font-display text-body-lg font-semibold text-obsidian">{title}</h3>
        {shortcut && <KbdChip>{shortcut}</KbdChip>}
      </div>
      <p className="text-body-sm text-slate">{description}</p>
    </a>
  );
}

// AvatarMenu lifted to AppShell — single source of truth for app chrome.
